/* eslint-disable no-param-reassign */

import BasicInfo from '@gameday/common/components/form/Pages/BasicInfo'
import { validAge, validFormAge, validGender } from '@gameday/common/factories/filter'
import { isUnknownDob } from '@gameday/common/functions/dob'
import Modal from '@gameday/react-components/components/Modal/Modal'
import { get, isEmpty, isEqual } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import config from '../../../../constants/config'
import { files } from '../../../../constants/endpoints'
import { privateRoutes } from '../../../../constants/routes/routes'
import { convertFormType } from '../../../../utils/common'

const StyledBasicInfo = styled.div`
  --custom-primary-color: ${props => props.formSettings.textLinkColour};

  .country-select__control\.country-select__control--is-focused {
    border-color: ${props => props.formSettings.textLinkColour} !important;
  }
`

const BasicInfoComponent = ({
  currentRegistration,
  memberTypes,
  questions,
  userId,
  form,
  clearParticipant,
  setValue,
  selectParticipant,
  defaultCountry,
  validationRules = {},
  ageRestriction,
  formType,
  email,
  formSettings,
  isTeamEvent,
  formInfo,
  members = [],
  timezone,
  ...rest
}) => {
  const [showValidationError, setShowValidationError] = useState(null)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [matchedMember, setMatchedMember] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const shouldBlockNavigation = useRef(false)

  const { next, ...restWithoutNext } = rest

  const wrappedNext = (...args) => {
    if (shouldBlockNavigation.current) {
      return
    }
    next(...args)
  }
  const [defaults, setDefaults] = useState({
    defaultMapping: {},
    defaultValues: {},
    revertMapping: {},
  })
  const type = convertFormType(formType) || convertFormType(formInfo?.form?.type)
  const renderDefaultMapping = () => {
    const questionMap = {
      photo: 'Profile Image',
      firstname: 'Firstname',
      lastname: 'Lastname',
      birthdate: 'Birthdate',
      gender: 'Gender',
      genderIdentity: 'Gender Identity',
      genderIdentityOther: 'Gender Identity - Other',
      email: 'Email',
      emailConfirmation: 'Email Confirmation',
      mobile: 'Mobile Number',
      memberTypes: 'Member Types',
      // registerAs: 'Who are you registering?',
    }
    const registration = isEmpty(currentRegistration?.email) ? { ...currentRegistration, email } : currentRegistration
    const defaultMapping = {}
    const defaultValues = {}
    const revertMapping = {}
    Object.keys(questionMap).forEach(key => {
      const sfid = get(
        questions.find(q => get(q, 'relatedQuestion.name') === questionMap[key]),
        'relatedQuestion.sfid'
      )

      if (!sfid) {
        return
      }

      defaultValues[sfid] = key === 'birthdate' && isUnknownDob(registration[key]) ? '' : registration[key]
      defaultMapping[sfid] = key
      revertMapping[key] = sfid
    })

    setDefaults({
      defaultMapping,
      defaultValues,
      revertMapping,
    })
  }
  useEffect(() => {
    renderDefaultMapping()
  }, [currentRegistration?.sfid])

  const resetCurrentProducts = (currentMemberTypes = []) => {
    // If there is a change to the member types, selected products needs to be reset.
    const cachedMemberTypes = currentRegistration?.memberTypes || []
    let isDifferent = cachedMemberTypes.length !== currentMemberTypes.length

    if (!isDifferent && cachedMemberTypes.length > 0) {
      const cachedMemberTypeIds = cachedMemberTypes.map(x => x.sfid).sort()
      const currentMemberTypeIds = currentMemberTypes.map(x => x.sfid).sort()
      isDifferent = !isEqual(currentMemberTypeIds, cachedMemberTypeIds)
    }

    if (isDifferent && (currentRegistration?.newProducts || currentRegistration?.newAddOnProducts)) {
      setValue({ newProducts: {}, newAddOnProducts: {} })
    }
  }

  const normalizeString = str => (str || '').toString().trim().toLowerCase()

  const checkForDuplicateProfile = formValues => {
    if (!currentRegistration?.isNewMember) {
      return null
    }

    const firstname = formValues[defaults.revertMapping.firstname]
    const lastname = formValues[defaults.revertMapping.lastname]
    const birthdate = formValues[defaults.revertMapping.birthdate]

    if (!firstname || !lastname || !birthdate) {
      return null
    }

    return members.find(
      member =>
        normalizeString(member.firstname) === normalizeString(firstname) &&
        normalizeString(member.lastname) === normalizeString(lastname) &&
        normalizeString(member.birthdate) === normalizeString(birthdate)
    )
  }

  const handleGoToProfile = () => {
    if (!matchedMember) return

    setIsLoading(true)

    setTimeout(() => {
      // Clear the new participant registration
      clearParticipant(currentRegistration?.sfid)
      // Select the matched member profile
      // Normalize the member to have sfid (reducer expects sfid, but members may have id)
      selectParticipant({
        ...matchedMember,
        sfid: matchedMember.id || matchedMember.sfid,
      })
      // Close modal and reset state
      setShowDuplicateModal(false)
      setMatchedMember(null)
      setIsLoading(false)
    }, 400)
  }

  return (
    <>
      <StyledBasicInfo formSettings={formSettings}>
        <BasicInfo
          key={currentRegistration?.sfid}
          formType={type}
          questions={questions}
          formData={{
            birthdate: isUnknownDob(currentRegistration?.birthdate) ? '' : currentRegistration?.birthdate,
            gender: currentRegistration?.gender,
            genderIdentity: currentRegistration?.genderIdentity,
            genderIdentityOther: currentRegistration?.genderIdentityOther,
            memberTypes: currentRegistration?.memberTypes,
            userId,
            form,
            // biome-ignore lint/complexity/useOptionalChain: TODO
            pageName: (privateRoutes(isTeamEvent).find(r => r.id === 'basicInfo') || {}).title || 'Basic Info',
            profileId: currentRegistration?.relatedMemberProfileId,
            application: config.application,
            uploadEndpoint: `api/${files.uploadDirect}`,
            documentUploadEndpoint: `api/${files.documentUploadDirect}`,
            downloadEndpoint: `api/${files.downloadDirect}`,
            allowOnlyOneMemberType: formSettings.allowOnlyOneMemberType,
          }}
          onSubmit={values => {
            // Check for duplicate profile before proceeding
            const duplicate = checkForDuplicateProfile(values)
            if (duplicate) {
              shouldBlockNavigation.current = true // Sync ref update to block next()
              setMatchedMember(duplicate)
              setShowDuplicateModal(true)
              return
            }
            shouldBlockNavigation.current = false // Allow navigation

            const mappedValue = Object.keys(values || {}).reduce(
              (a, v) => ({
                // biome-ignore lint/performance/noAccumulatingSpread: TODO
                ...a,
                [defaults.defaultMapping[v]]:
                  typeof values[v] === 'object' && 'value' in values[v] ? values[v].value : values[v],
              }),
              {}
            )

            // ensure member's gender is included in registration
            // to avoid duplicate membership creation
            if (typeof mappedValue.gender === 'undefined') {
              mappedValue.gender = currentRegistration?.gender || ''
            }

            resetCurrentProducts(mappedValue.memberTypes)
            setValue(mappedValue)
          }}
          defaultValue={defaults.defaultValues}
          memberTypes={memberTypes}
          disableMemberType={type?.toLowerCase() === 'shop'}
          onBack={() => {
            if (!currentRegistration?.isComplete) {
              clearParticipant(currentRegistration?.sfid)
            }
          }}
          disableQuestions={!currentRegistration?.isNewMember}
          defaultCountry={defaultCountry}
          onChange={values => {
            const gender = values[defaults.revertMapping.gender]
            const birthdate = values[defaults.revertMapping.birthdate]
            if (gender && birthdate) {
              if (!validFormAge(ageRestriction, birthdate, timezone)) {
                setShowValidationError('form_age')
              } else if (!validGender(validationRules.gender, gender) || !validAge(validationRules, birthdate)) {
                setShowValidationError('display_rule')
              } else {
                setShowValidationError(null)
              }
            }
          }}
          showValidationError={showValidationError}
          next={wrappedNext}
          {...restWithoutNext}
        />
      </StyledBasicInfo>

      <Modal
        isOpen={showDuplicateModal}
        label="Duplicate Profile Detected"
        handleModalClose={() => {
          setShowDuplicateModal(false)
          setMatchedMember(null)
        }}
        handleModalSubmit={handleGoToProfile}
        okText="Use existing profile"
        cancelText="Cancel"
        isShowBottomBar
        isShowSaveButton
        disableSaveButton={isLoading}
      >
        <p style={{ paddingTop: '0.5rem' }}>You already have a profile that matches these details.</p>
        <br />
      </Modal>
    </>
  )
}

export default BasicInfoComponent
